import React, { useCallback, useMemo, useState } from 'react';
import Link from 'next/link';
import Image from 'next/legacy/image';
import { useRouter } from 'next/router';
import styled, { withTheme, DefaultTheme } from 'styled-components';
import { Row, Col, Tooltip, Tag, Carousel, Button } from 'antd';
import { MailOutlined, PhoneOutlined } from '@ant-design/icons';
import TextClamp from 'react-string-clamp';

import { formatCurrency } from '../../common/helpers';
import {
  getFlatDescription,
  getFlatSlug,
} from '../../common/helpers/flat.utils';
import * as flatTypeUtils from '../../common/helpers/flatType.utils';
import useMailchimpService from '../../common/hooks/mailchimpService';
import useI18n from '../../common/hooks/useI18n';
import { IFlat } from '../../common/model/flat.model';
import { IContact } from '../../common/model/mailchimp/contact.model';
import { IOffice } from '../../common/model/office.model';
import { MailchimpInterest } from '../../common/model/mailchimp/enums/mailchimpInterest.enum';

import RequestInfoButton from '../flatDetail/requestInfoButton';

type Props = {
  flat: IFlat;
  office?: IOffice;
  cardBackgroundColor?: string;
  forceVerticalMode?: boolean;
  useCarousel?: boolean;
  newTab?: boolean;
  onMouseEnter?: () => void;
  className?: string;
  theme: DefaultTheme;
};

const Container = styled.div`
  color: inherit;

  .ant-carousel {
    line-height: 0;
    height: 100%;

    & > .slick-slider {
      height: 100%;

      & > .slick-list {
        height: 100%;

        & > .slick-track {
          height: 100%;

          & > .slick-slide {
            height: 100%;

            & > div {
              height: 100%;
            }
          }
        }
      }
    }

    .slick-arrow {
      width: 40px !important;
      height: 40px !important;
      z-index: 1;

      &:before {
        display: none;
      }
    }
  }

  @media ${(props) => props.theme.breakpoints.smd} {
    .ant-carousel .slick-arrow {
      display: block !important;
    }
  }
`;

const FlatImageContainer = styled.div<{
  $forceVerticalMode: boolean;
  $isClosed: boolean;
}>`
  position: relative;
  width: 100%;
  height: ${(props) => (props.$forceVerticalMode ? '24vh' : '100%')};
  min-height: ${(props) => (props.$forceVerticalMode ? '200px' : 'unset')};
  max-height: ${(props) => (props.$forceVerticalMode ? '300px' : 'unset')};
  filter: ${(props) =>
    props.$isClosed ? 'grayscale(90%) opacity(0.8)' : 'none'};

  @media ${(props) => props.theme.breakpoints.mdd} {
    height: 24vh;
    min-height: 200px;
    max-height: 300px;
  }
`;

const FlatImage = styled(Image)<{ $forceVerticalMode: boolean }>`
  border-top-left-radius: ${(props) => props.theme.borderRadius};
  border-top-right-radius: ${(props) =>
    props.$forceVerticalMode ? props.theme.borderRadius : '0'};
  border-bottom-left-radius: ${(props) =>
    props.$forceVerticalMode ? '0' : props.theme.borderRadius};

  @media ${(props) => props.theme.breakpoints.mdd} {
    border-top-right-radius: ${(props) => props.theme.borderRadius};
    border-bottom-left-radius: 0;
  }
`;

const PrevArrow = styled.div`
  background-image: url(/images/prev.svg) !important;
  left: 4px !important;
`;

const NextArrow = styled.div`
  background-image: url(/images/next.svg) !important;
  right: 4px !important;
`;

const SlideCountTag = styled(Tag)`
  position: absolute;
  bottom: 12px;
  left: 12px;
  margin: 0;
  height: 32px;
  font-size: 12px;
  line-height: 32px;
`;

const TopRightTag = styled(Tag)`
  position: absolute;
  top: 12px;
  right: 12px;
  margin: 0;
  font-weight: 500;
  font-size: 12px;
  line-height: 32px;
`;

const VirtualVisitTag = styled(TopRightTag)`
  height: 32px;
  width: 32px;
  cursor: pointer;
`;

const InfoContainer = styled.div<{ $cardBackgroundColor: string }>`
  height: 100%;
  padding: 24px;
  background-color: ${(props) => props.$cardBackgroundColor};
  color: ${(props) => props.theme.colors.secondary};

  &:hover,
  &:active {
    color: ${(props) => props.theme.colors.secondary};
  }

  @media ${(props) => props.theme.breakpoints.smd} {
    padding: 16px;
  }
`;

const DivWithEllipsis = styled.div`
  display: flex;

  div {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const Title = styled.div`
  margin-bottom: 8px;
  font-size: 16px;
  margin-bottom: 8px;
  font-weight: 500;

  @media ${(props) => props.theme.breakpoints.smd} {
    margin-bottom: 4px;
  }
`;

const PriceText = styled.div`
  margin-top: 4px;
  font-size: 24px;
  line-height: 1.5;
  font-weight: 700;
`;

const FeaturesSection = styled.div`
  margin-top: 16px;
`;

const FeatureAtTopCol = styled(Col)<{ $borderVisible: boolean }>`
  padding-bottom: ${(props) => (props.$borderVisible ? '16px' : '0')};
  border-bottom: ${(props) => (props.$borderVisible ? '1px' : '0')} solid
    ${(props) => props.theme.colors.grey};

  @media ${(props) => props.theme.breakpoints.mdd} {
    padding-bottom: 0;
    border-bottom-width: 0;
  }
`;

const FeatureAtBottomCol = styled(Col)<{
  $forceVerticalMode: boolean;
  $visible: boolean;
}>`
  display: ${(props) => (props.$forceVerticalMode ? 'none' : 'block')};
  visibility: ${(props) => (props.$visible ? 'visible' : 'hidden')};

  @media ${(props) => props.theme.breakpoints.mdd} {
    display: none;
  }
`;

const FeatureInfo = styled.div`
  font-size: 14px;
  line-height: 110%;

  @media ${(props) => props.theme.breakpoints.xs} {
    font-size: 12px;
  }
`;

const FeatureIcon = styled.div`
  position: absolute;
  margin-top: -8px;
`;

const FeatureText = styled.span`
  margin-left: 32px;
`;

const VerticalModeDescriptionContainer = styled.div<{
  $forceVerticalMode: boolean;
}>`
  padding-bottom: ${(props) => (props.$forceVerticalMode ? '0' : '17px')};

  @media ${(props) => props.theme.breakpoints.mdd} {
    padding-bottom: 0;
  }
`;

const Description = styled.div`
  margin-top: 4px;
  line-height: 120%;
  height: calc(14px * 2.4);

  @media ${(props) => props.theme.breakpoints.xs} {
    height: calc(12px * 2.4);
  }
`;

const BottomInfoSection = styled.div`
  margin-top: 16px;
`;

const StyledTooltip = styled(Tooltip)`
  .ant-tooltip-inner {
    background-color: ${(props) => props.theme.colors.secondary};
  }
`;

const StyledAnchor = styled.a`
  color: inherit;
  &:hover {
    color: inherit;
  }
`;

const FlatCard = ({
  flat,
  office,
  cardBackgroundColor = 'inherit',
  forceVerticalMode = false,
  useCarousel = false,
  newTab = false,
  onMouseEnter,
  className,
  theme,
}: Props): JSX.Element => {
  const router = useRouter();
  const i18n = useI18n();
  const mailchimpService = useMailchimpService();

  const [currentSlide, setCurrentSlide] = useState(0);

  const title = useMemo(
    () =>
      i18n.t('flat.title', {
        type: i18n.t(flatTypeUtils.getFlatTypeLabel(flat.type)),
        address: flat.address,
      }),
    [flat.address, flat.type, i18n]
  );

  const zoneFormatted = useMemo(() => `${flat.zone} (${flat.city})`, [
    flat.city,
    flat.zone,
  ]);

  const getImageElement = useCallback(
    (src: string) => (
      <Image src={src} layout="fixed" width={28} height={28} alt={src} />
    ),
    []
  );

  const features = useMemo(() => {
    const features: JSX.Element[] = [
      <>
        <FeatureIcon>
          {getImageElement('/images/flat/square_meters.png')}
        </FeatureIcon>
        <FeatureText>
          {flat.sqrMeters} m
          <sup style={{ verticalAlign: 'top', fontSize: '0.6em' }}>2</sup>
        </FeatureText>
      </>,
    ];
    if (flat.rooms > 0) {
      features.push(
        <>
          <FeatureIcon>{getImageElement('/images/flat/room.png')}</FeatureIcon>
          <FeatureText>
            {flat.rooms}{' '}
            {i18n
              .t('flat.roomsShort')
              .toLowerCase()
              .replaceAll('s', flat.rooms === 1 ? '' : 's')}
          </FeatureText>
        </>
      );
    }
    if (flat.bathrooms > 0) {
      features.push(
        <>
          <FeatureIcon>
            {getImageElement('/images/flat/bathroom.png')}
          </FeatureIcon>
          <FeatureText>
            {flat.bathrooms}{' '}
            {i18n
              .t('flat.bathrooms')
              .toLowerCase()
              .replaceAll('s', flat.bathrooms === 1 ? '' : 's')}
          </FeatureText>
        </>
      );
    }
    if (flat.hasElevator) {
      features.push(
        <>
          <FeatureIcon>
            {getImageElement('/images/flat/elevator.png')}
          </FeatureIcon>
          <FeatureText>{i18n.t('flat.elevator')}</FeatureText>
        </>
      );
    }
    if (flat.hasGarden) {
      features.push(
        <>
          <FeatureIcon>
            {getImageElement('/images/flat/garden.png')}
          </FeatureIcon>
          <FeatureText>{i18n.t('flat.garden')}</FeatureText>
        </>
      );
    }
    if (flat.hasTerrace) {
      features.push(
        <>
          <FeatureIcon>
            {getImageElement('/images/flat/garden.png')}
          </FeatureIcon>
          <FeatureText>{i18n.t('flat.terrace')}</FeatureText>
        </>
      );
    }
    if (flat.hasBalcony) {
      features.push(
        <>
          <FeatureIcon>
            {getImageElement('/images/flat/balcony.png')}
          </FeatureIcon>
          <FeatureText>{i18n.t('flat.balcony')}</FeatureText>
        </>
      );
    }
    if (flat.hasGarage) {
      features.push(
        <>
          <FeatureIcon>
            {getImageElement('/images/flat/garage.png')}
          </FeatureIcon>
          <FeatureText>{i18n.t('flat.garage')}</FeatureText>
        </>
      );
    }
    if (flat.hasSwimmingPool) {
      features.push(
        <>
          <FeatureIcon>
            {getImageElement('/images/flat/swimming_pool.png')}
          </FeatureIcon>
          <FeatureText>{i18n.t('flat.swimmingPool')}</FeatureText>
        </>
      );
    }
    return features;
  }, [
    flat.bathrooms,
    flat.hasBalcony,
    flat.hasElevator,
    flat.hasGarage,
    flat.hasGarden,
    flat.hasSwimmingPool,
    flat.hasTerrace,
    flat.rooms,
    flat.sqrMeters,
    getImageElement,
    i18n,
  ]);

  const borderVisible = useMemo(
    () => !forceVerticalMode && features.length > 3,
    [features, forceVerticalMode]
  );

  const description = useMemo(
    () => (
      <FeatureInfo>
        <Description>
          <TextClamp
            text={flat.titleWeb || getFlatDescription(flat, i18n.activeLocale)}
            lines={2}
            element="p"
          />
        </Description>
      </FeatureInfo>
    ),
    [flat, i18n.activeLocale]
  );

  const onContactButtonClick = useCallback(
    (contact: IContact) => {
      const handleBuyButtonClick = (contact: IContact) => {
        contact.DFLAT = `${flat.address} - ${flat.zone} - ${flat.city}`;
        if (office) {
          contact.officeEmail = office.email;
        }
        mailchimpService.subscribe(
          { ...contact, interests: [MailchimpInterest.COMPRAR] },
          router,
          i18n
        );
      };

      handleBuyButtonClick(contact);
    },
    [flat.address, flat.city, flat.zone, i18n, mailchimpService, office, router]
  );

  const getLink = useCallback(
    (children: React.ReactNode) => {
      if (flat.isClosed) {
        return children;
      }

      return (
        <Link
          href={`/comprar/${flatTypeUtils.getFlatTypeSlug(
            flat.type
          )}/${getFlatSlug(flat)}`}
          passHref
          target={newTab ? '_blank' : undefined}
          legacyBehavior
        >
          <StyledAnchor>{children}</StyledAnchor>
        </Link>
      );
    },
    [flat, newTab]
  );

  return (
    <div className={className} onMouseEnter={onMouseEnter}>
      <Container>
        <Row>
          <Col xs={24} lg={forceVerticalMode ? 24 : 8}>
            <div
              style={{
                position: 'relative',
                width: '100%',
                height: '100%',
              }}
            >
              {flat.pictureUrls.length > 1 && useCarousel
                ? getLink(
                    <Carousel
                      dots={false}
                      draggable={true}
                      lazyLoad={'ondemand'}
                      arrows={true}
                      prevArrow={<PrevArrow />}
                      nextArrow={<NextArrow />}
                      beforeChange={(_, nextSlide) =>
                        setCurrentSlide(nextSlide)
                      }
                      afterChange={setCurrentSlide}
                    >
                      {flat.pictureUrls.map((url) => (
                        <FlatImageContainer
                          key={url}
                          as="div"
                          $forceVerticalMode={forceVerticalMode}
                          $isClosed={flat.isClosed}
                        >
                          <FlatImage
                            alt={flat.address}
                            layout="fill"
                            objectFit="cover"
                            src={url}
                            sizes={theme.getImageSizes(
                              '100vw',
                              '100vw',
                              '100vw',
                              forceVerticalMode ? '100vw' : '33vw'
                            )}
                            $forceVerticalMode={forceVerticalMode}
                          />
                        </FlatImageContainer>
                      ))}
                    </Carousel>
                  )
                : getLink(
                    <FlatImageContainer
                      as="div"
                      $forceVerticalMode={forceVerticalMode}
                      $isClosed={flat.isClosed}
                    >
                      <FlatImage
                        alt={flat.address}
                        layout="fill"
                        objectFit="cover"
                        src={flat.pictureUrls[0]}
                        sizes={theme.getImageSizes(
                          '100vw',
                          '100vw',
                          '100vw',
                          forceVerticalMode ? '100vw' : '33vw'
                        )}
                        $forceVerticalMode={forceVerticalMode}
                      />
                    </FlatImageContainer>
                  )}
              <SlideCountTag color="grey">
                {currentSlide + 1} / {flat.pictureUrls.length}
              </SlideCountTag>
              {flat.matterportUrl && !flat.isClosed && (
                <StyledTooltip
                  title={i18n.t('flatCard.messages.virtualVisitAvailable')}
                  placement="left"
                >
                  <VirtualVisitTag color={theme.colors.primary}>
                    <span>3D</span>
                  </VirtualVisitTag>
                </StyledTooltip>
              )}
              {flat.isClosed && (
                <TopRightTag color={theme.colors.primary}>
                  <span>VENDIDO</span>
                </TopRightTag>
              )}
            </div>
          </Col>
          <Col xs={24} lg={forceVerticalMode ? 24 : 16}>
            <InfoContainer as="div" $cardBackgroundColor={cardBackgroundColor}>
              {getLink(
                <DivWithEllipsis>
                  <StyledTooltip title={title}>
                    <Title>{title}</Title>
                  </StyledTooltip>
                </DivWithEllipsis>
              )}

              {getLink(
                <DivWithEllipsis>
                  <StyledTooltip title={zoneFormatted}>
                    <FeatureInfo>{zoneFormatted}</FeatureInfo>
                  </StyledTooltip>
                </DivWithEllipsis>
              )}

              {getLink(
                <PriceText>
                  {formatCurrency(flat.price, i18n.activeLocale)}
                </PriceText>
              )}

              <FeaturesSection>
                <Row
                  gutter={[
                    16,
                    { xs: 8, sm: 8, md: 8, lg: 16, xl: 16, xxl: 16 },
                  ]}
                >
                  <FeatureAtTopCol
                    span={forceVerticalMode ? 12 : 8}
                    $borderVisible={borderVisible}
                  >
                    <FeatureInfo>{features[0]}</FeatureInfo>
                  </FeatureAtTopCol>
                  <FeatureAtTopCol
                    $borderVisible={borderVisible}
                    span={forceVerticalMode ? 12 : 8}
                  >
                    <FeatureInfo>{features[1]}</FeatureInfo>
                  </FeatureAtTopCol>
                  <FeatureAtTopCol
                    span={forceVerticalMode ? 0 : 8}
                    $borderVisible={borderVisible}
                  >
                    <FeatureInfo>{features[2]}</FeatureInfo>
                  </FeatureAtTopCol>
                  {(forceVerticalMode || !features[3]) && (
                    <Col span={24}>
                      <VerticalModeDescriptionContainer
                        as="div"
                        $forceVerticalMode={forceVerticalMode}
                      >
                        {description}
                      </VerticalModeDescriptionContainer>
                    </Col>
                  )}
                  <FeatureAtBottomCol
                    span={8}
                    $forceVerticalMode={forceVerticalMode}
                    $visible={!!features[3]}
                  >
                    <FeatureInfo>{features[3] ?? features[0]}</FeatureInfo>
                  </FeatureAtBottomCol>
                  <FeatureAtBottomCol
                    span={8}
                    $forceVerticalMode={forceVerticalMode}
                    $visible={!!features[4]}
                  >
                    <FeatureInfo>{features[4] ?? features[0]}</FeatureInfo>
                  </FeatureAtBottomCol>
                  <FeatureAtBottomCol
                    span={8}
                    $forceVerticalMode={forceVerticalMode}
                    $visible={!!features[5]}
                  >
                    <FeatureInfo>{features[5] ?? features[0]}</FeatureInfo>
                  </FeatureAtBottomCol>
                  {!(forceVerticalMode || !features[3]) && (
                    <Col span={24}>{description}</Col>
                  )}
                </Row>
              </FeaturesSection>
              {!flat.isClosed && (
                <BottomInfoSection>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <a href="/#">
                      <RequestInfoButton
                        onButtonClick={onContactButtonClick}
                        text={
                          <>
                            <MailOutlined />{' '}
                            {i18n.t('flatDetail.actions.requestInfoShort')}
                          </>
                        }
                        size="middle"
                        limitWidth
                      />
                    </a>
                    {office && (
                      <a
                        href={`tel:${office.phone}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Button htmlType="button" type="default" size="middle">
                          <PhoneOutlined
                            style={{ transform: 'rotate(90deg)' }}
                          />{' '}
                          {i18n.t('flatDetail.actions.call')}
                        </Button>
                      </a>
                    )}
                  </div>
                </BottomInfoSection>
              )}
            </InfoContainer>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default withTheme<typeof FlatCard>(styled(FlatCard)`
  background-color: white;
  box-shadow: 0px 6px 21px -4px rgba(0, 0, 0, 0.25);
  border-radius: ${(props) => props.theme.borderRadius};
  overflow: hidden;
`);
